import { useEffect, useState } from 'react';

/**
 * The browser's own "install this app" prompt, surfaced inside the app.
 *
 * Chrome fires `beforeinstallprompt` when the page qualifies for installation
 * and lets the page defer it. Relying on the browser menu instead is what left
 * a user stuck: removing the home-screen icon does not uninstall the app, so
 * Chrome kept saying "Open in app" and never offered to install again, with no
 * way back to the icon from inside the product.
 *
 * iOS does not implement any of this. Safari installs only through the Share
 * sheet, so there the answer is instructions, not a button.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * The event is caught at module load, not in an effect.
 *
 * Chrome fires `beforeinstallprompt` once, within milliseconds of the page
 * loading — reliably before React has mounted and a `useEffect` has run. A hook
 * that only subscribes on mount misses it and then reports, for the rest of the
 * session, that installation is unavailable. Which is exactly the wrong answer
 * for a user who came looking for the install button.
 *
 * So the listener is installed the moment this module is imported, and the hook
 * reads what it caught. `captureInstallPrompt` is called from main.tsx before
 * the first render to guarantee the import has happened by then.
 */
let captured: BeforeInstallPromptEvent | undefined;
let installed = false;
const subscribers = new Set<() => void>();

function notify() {
  for (const fn of subscribers) fn();
}

export function captureInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Without this the browser shows its own banner and spends the event before
    // the button in My details can ever use it.
    event.preventDefault();
    captured = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    installed = true;
    captured = undefined;
    notify();
  });
}

export type InstallState =
  /** Already running as an installed app. Nothing to offer. */
  | 'installed'
  /** The browser has offered a prompt and we are holding it. */
  | 'available'
  /** Installable, but only through the Share sheet — iOS Safari. */
  | 'manual-ios'
  /** No prompt, and not iOS. Either already installed or not qualifying. */
  | 'unavailable';

function runningStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS predates the display-mode media query and uses its own flag.
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

function isIosSafari(): boolean {
  const ua = window.navigator.userAgent;
  // iPadOS 13+ reports itself as a Mac, so a touch-capable "Mac" is an iPad.
  const iOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  // Chrome and Firefox on iOS cannot install at all; only Safari can.
  return iOS && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

export function useInstallPrompt(): {
  state: InstallState;
  install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
} {
  // Re-render when the module-level capture changes, rather than owning it.
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    subscribers.add(fn);
    return () => {
      subscribers.delete(fn);
    };
  }, []);

  const state: InstallState =
    installed || runningStandalone()
      ? 'installed'
      : captured
        ? 'available'
        : isIosSafari()
          ? 'manual-ios'
          : 'unavailable';

  async function install() {
    if (!captured) return 'unavailable' as const;
    await captured.prompt();
    const { outcome } = await captured.userChoice;
    // A prompt is single-use whatever the answer; holding it would leave a
    // button that silently does nothing the second time.
    captured = undefined;
    notify();
    return outcome;
  }

  return { state, install };
}
